import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Loader2, Zap, History, BadgePercent, Coins, CalendarClock,
} from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';
import { useAuth } from '../../context/AuthContext';
import { formatRM } from '../../lib/format';
import PageHeader from '../../components/PageHeader';
import EmptyState from '../../components/EmptyState';
import StatusPill from '../../components/StatusPill';
import MonthPicker from '../../components/MonthPicker';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { CenterMorphModal, CenterMorphModalContent } from '../../components/beui/CenterMorphModal';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';

const JENIS = ['bulanan', 'tahunan', 'sekali'];

function nowPeriodeKL() {
  try {
    return new Date().toLocaleString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }).slice(0, 7);
  } catch { return new Date().toISOString().slice(0, 7); }
}
function nowYearKL() {
  try {
    return new Date().toLocaleString('en-CA', { timeZone: 'Asia/Kuala_Lumpur' }).slice(0, 4);
  } catch { return String(new Date().getFullYear()); }
}

export default function Tarif() {
  const { t } = useTranslation();
  const { school } = useAuth();
  const [tab, setTab] = useState('tarif');
  const [loading, setLoading] = useState(true);
  const [tariffs, setTariffs] = useState([]);
  const [rules, setRules] = useState([]);

  // dialog tarif
  const [dlgTarif, setDlgTarif] = useState(null); // null | {…row} | {new:true}
  const [fNama, setFnama] = useState('');
  const [fJumlah, setFjumlah] = useState('');
  const [fJenis, setFjenis] = useState('bulanan');
  const [fLayak, setFlayak] = useState(true);
  // dialog peraturan
  const [dlgRule, setDlgRule] = useState(null);
  const [rNama, setRnama] = useState('Diskaun Adik-Beradik');
  const [rJenis, setRjenis] = useState('peratus');
  const [rNilai, setRnilai] = useState('25');
  const [rAnakKe, setRanakKe] = useState('2');
  const [busy, setBusy] = useState(false);

  // jana bil
  const [bJenis, setBjenis] = useState('bulanan');
  const [bPeriode, setBperiode] = useState(nowPeriodeKL());
  const [bPreview, setBpreview] = useState(null);
  const [bRunning, setBrunning] = useState(false);
  const [bResult, setBresult] = useState(null);
  const [bHistory, setBhistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [{ data: tr }, { data: rl }] = await Promise.all([
      supabase.from('tariffs').select('*').order('jenis').order('nama'),
      supabase.from('discount_rules').select('*').order('created_at'),
    ]);
    setTariffs(tr || []); setRules(rl || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadHistory = useCallback(async () => {
    const { data } = await supabase.from('audit_logs')
      .select('id, created_at, detail_json, actor:actor_id(nama)')
      .eq('aksi', 'generate_billing')
      .order('created_at', { ascending: false }).limit(10);
    setBhistory(data || []);
  }, []);
  useEffect(() => { if (tab === 'bil') loadHistory(); }, [tab, loadHistory]);

  // ---------- tarif CRUD ----------
  const openTarif = (row) => {
    setDlgTarif(row || { isNew: true });
    setFnama(row?.nama || ''); setFjumlah(row?.jumlah_rm ?? '');
    setFjenis(row?.jenis || 'bulanan'); setFlayak(row?.layak_diskon_sibling ?? true);
  };
  const saveTarif = async () => {
    const jumlah = Number(fJumlah);
    if (!fNama.trim() || !(jumlah > 0)) { toast.error(t('lengkapkanMaklumat')); return; }
    setBusy(true);
    const payload = { school_id: school.id, nama: fNama.trim(), jumlah_rm: jumlah, jenis: fJenis, layak_diskon_sibling: fLayak };
    const { error } = dlgTarif?.isNew
      ? await supabase.from('tariffs').insert(payload)
      : await supabase.from('tariffs').update(payload).eq('id', dlgTarif.id);
    setBusy(false);
    if (error) { toast.error(t('ralat')); return; }
    toast.success(t('disimpan')); setDlgTarif(null); load();
  };
  const delTarif = async (row) => {
    if (!window.confirm(`${t('padam')} "${row.nama}"?`)) return;
    const { error } = await supabase.from('tariffs').delete().eq('id', row.id);
    if (error) { toast.error(t('ralat')); return; }
    toast.success(t('dipadam')); load();
  };

  // ---------- peraturan CRUD ----------
  const openRule = (row) => {
    setDlgRule(row || { isNew: true });
    setRnama(row?.nama || 'Diskaun Adik-Beradik');
    setRjenis(row?.jenis || 'peratus');
    setRnilai(row?.nilai ?? '25'); setRanakKe(row?.anak_ke_min ?? '2');
  };
  const saveRule = async () => {
    const nilai = Number(rNilai); const anakKe = parseInt(rAnakKe, 10);
    if (!rNama.trim() || !(nilai > 0) || !(anakKe >= 2)) { toast.error(t('lengkapkanMaklumat')); return; }
    setBusy(true);
    const payload = { school_id: school.id, nama: rNama.trim(), jenis: rJenis, nilai, anak_ke_min: anakKe };
    const { error } = dlgRule?.isNew
      ? await supabase.from('discount_rules').insert({ ...payload, aktif: true })
      : await supabase.from('discount_rules').update(payload).eq('id', dlgRule.id);
    setBusy(false);
    if (error) { toast.error(t('ralat')); return; }
    toast.success(t('disimpan')); setDlgRule(null); load();
  };
  const toggleRule = async (row) => {
    const { error } = await supabase.from('discount_rules').update({ aktif: !row.aktif }).eq('id', row.id);
    if (error) { toast.error(t('ralat')); return; }
    load();
  };
  const delRule = async (row) => {
    if (!window.confirm(`${t('padam')} "${row.nama}"?`)) return;
    const { error } = await supabase.from('discount_rules').delete().eq('id', row.id);
    if (error) { toast.error(t('ralat')); return; }
    toast.success(t('dipadam')); load();
  };

  // ---------- jana bil ----------
  useEffect(() => {
    setBperiode(bJenis === 'bulanan' ? nowPeriodeKL() : nowYearKL());
    setBresult(null); setBpreview(null);
  }, [bJenis]);
  const previewBil = async () => {
    const [{ count: cPelajar }, { count: cTarif }] = await Promise.all([
      supabase.from('students').select('id', { count: 'exact', head: true }).eq('aktif', true),
      supabase.from('tariffs').select('id', { count: 'exact', head: true }).eq('jenis', bJenis),
    ]);
    setBpreview({ pelajar: cPelajar || 0, tarif: cTarif || 0 });
  };
  useEffect(() => { if (tab === 'bil') previewBil(); }, [tab, bJenis]); // eslint-disable-line
  const runBilling = async () => {
    if (!window.confirm(t('sahkanJanaBil', { periode: bPeriode }))) return;
    setBrunning(true); setBresult(null);
    const { data, error } = await supabase.rpc('generate_billing', {
      p_school: school.id, p_periode: bPeriode, p_jenis: bJenis,
    });
    setBrunning(false);
    if (error) { toast.error(t('ralat')); return; }
    setBresult(data); loadHistory();
    toast.success(t('bilBerjaya', { n: data.dibuat }));
  };

  const jenisPill = (j) => (
    <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold ${
      j === 'bulanan' ? 'bg-brand-light text-brand' : j === 'tahunan' ? 'bg-amber2-light text-amber-700 dark:text-amber-300' : 'bg-muted text-muted-foreground'
    }`}>{t(j)}</span>
  );

  return (
    <div>
      <PageHeader
        title={t('tetapan')}
        subtitle={t('tetapanSub')}
        breadcrumbs={[{ label: t('pengurusan') }, { label: t('tetapan') }]}
      />

      <Tabs value={tab} onValueChange={setTab} className="mt-2">
        <TabsList className="h-11 sm:h-12">
          <TabsTrigger value="tarif" className="h-9 sm:h-10" data-testid="tab-tarif">
            <Coins className="h-4 w-4 mr-1.5 sm:mr-2" /><span className="sm:hidden">{t('tarifPendek')}</span><span className="hidden sm:inline">{t('senaraiTarif')}</span>
          </TabsTrigger>
          <TabsTrigger value="diskon" className="h-9 sm:h-10" data-testid="tab-diskon">
            <BadgePercent className="h-4 w-4 mr-1.5 sm:mr-2" /><span className="sm:hidden">{t('diskonPendek')}</span><span className="hidden sm:inline">{t('peraturanDiskon')}</span>
          </TabsTrigger>
          <TabsTrigger value="bil" className="h-9 sm:h-10" data-testid="tab-bil">
            <Zap className="h-4 w-4 mr-1.5 sm:mr-2" />{t('janaBil')}
          </TabsTrigger>
        </TabsList>

        {/* ===== TAB TARIF ===== */}
        <TabsContent value="tarif" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between gap-3">
              <div className="min-w-0 flex-1"><CardTitle>{t('senaraiTarif')}</CardTitle>
              <CardDescription>{t('senaraiTarifSub')}</CardDescription></div>
              <Button onClick={() => openTarif(null)} className="bg-brand text-white hover:bg-brand-royal h-11 shrink-0" data-testid="tambah-tarif">
                <Plus className="h-4 w-4 mr-2" />{t('tambah')}
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? <p className="p-6 text-muted-foreground">{t('memuatkan')}</p>
              : tariffs.length === 0 ? <EmptyState title={t('tiadaData')} />
              : (
                <div className="divide-y divide-border">
                  {tariffs.map((x) => (
                    <div key={x.id} className="flex items-center gap-4 p-4 hover:bg-muted" data-testid={`tarif-row-${x.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-foreground">{x.nama}</p>
                        <div className="flex items-center gap-2 mt-1">{jenisPill(x.jenis)}
                          {x.layak_diskon_sibling && (
                            <span className="text-xs text-emerald2 font-medium">• {t('layakDiskon')}</span>
                          )}
                        </div>
                      </div>
                      <p className="font-bold text-foreground tabular-nums whitespace-nowrap shrink-0">{formatRM(x.jumlah_rm)}</p>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openTarif(x)} className="h-10 w-10 p-0" data-testid={`edit-tarif-${x.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => delTarif(x)} className="h-10 w-10 p-0 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/60" data-testid={`del-tarif-${x.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TAB PERATURAN DISKON ===== */}
        <TabsContent value="diskon" className="mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div><CardTitle>{t('peraturanDiskon')}</CardTitle>
              <CardDescription>{t('peraturanDiskonSub')}</CardDescription></div>
              <Button onClick={() => openRule(null)} className="bg-brand text-white hover:bg-brand-royal h-11" data-testid="tambah-rule">
                <Plus className="h-4 w-4 mr-2" />{t('tambah')}
              </Button>
            </CardHeader>
            <CardContent className="p-0">
              {loading ? <p className="p-6 text-muted-foreground">{t('memuatkan')}</p>
              : rules.length === 0 ? <EmptyState title={t('tiadaData')} />
              : (
                <div className="divide-y divide-border">
                  {rules.map((x) => (
                    <div key={x.id} className="flex items-center gap-4 p-4 hover:bg-muted" data-testid={`rule-row-${x.id}`}>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-foreground">{x.nama}</p>
                        <p className="text-sm text-muted-foreground mt-0.5">
                          {x.jenis === 'peratus' ? `${x.nilai}%` : formatRM(x.nilai)}
                          {' '}• {t('anakKeMin', { n: x.anak_ke_min })}
                        </p>
                      </div>
                      <Switch checked={x.aktif} onCheckedChange={() => toggleRule(x)} data-testid={`toggle-rule-${x.id}`} />
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => openRule(x)} className="h-10 w-10 p-0" data-testid={`edit-rule-${x.id}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => delRule(x)} className="h-10 w-10 p-0 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/60" data-testid={`del-rule-${x.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== TAB JANA BIL ===== */}
        <TabsContent value="bil" className="mt-4">
          <div className="grid lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><Zap className="h-5 w-5 text-amber2" />{t('janaBil')}</CardTitle>
                <CardDescription>{t('janaBilSub')}</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>{t('jenisTarif')}</Label>
                  <div className="grid grid-cols-2 gap-2 mt-1.5">
                    {['bulanan', 'tahunan'].map((j) => (
                      <button key={j} type="button" onClick={() => setBjenis(j)} data-testid={`bil-jenis-${j}`}
                        className={`h-12 rounded-xl border-2 text-sm font-semibold transition-colors ${bJenis === j ? 'border-brand bg-brand-light text-brand' : 'border-border text-muted-foreground hover:border-border'}`}>
                        {t(j)}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label htmlFor="periode">{t('periode')}</Label>
                  {bJenis === 'bulanan' ? (
                    <MonthPicker id="periode" value={bPeriode} onChange={setBperiode}
                      className="mt-1.5" testId="bil-periode" />
                  ) : (
                    <Input id="periode" type="number" min="2020" max="2100" value={bPeriode} onChange={(e) => setBperiode(e.target.value)}
                      className="h-12 mt-1.5" data-testid="bil-periode" />
                  )}
                </div>
                {bPreview && (
                  <div className="rounded-xl bg-muted border border-border p-4 text-sm text-muted-foreground" data-testid="bil-preview">
                    <CalendarClock className="h-4 w-4 inline mr-2 text-muted-foreground" />
                    {t('pratontonBil', { pelajar: bPreview.pelajar, tarif: bPreview.tarif, n: bPreview.pelajar * bPreview.tarif })}
                  </div>
                )}
                <Button onClick={runBilling} disabled={bRunning || !bPeriode}
                  className="w-full h-12 bg-brand text-white hover:bg-brand-royal text-base font-semibold" data-testid="run-billing">
                  {bRunning ? <Loader2 className="h-5 w-5 animate-spin mr-2" /> : <Zap className="h-5 w-5 mr-2" />}
                  {t('janaSekarang')}
                </Button>
                {bResult && (
                  <div className="rounded-xl border-2 border-emerald2-light bg-emerald2-light/40 p-4" data-testid="bil-result">
                    <p className="font-bold text-emerald2">{t('bilBerjaya', { n: bResult.dibuat })}</p>
                    <div className="grid grid-cols-3 gap-2 mt-3 text-center">
                      <div className="bg-card rounded-lg p-2"><p className="text-xl sm:text-2xl font-bold text-foreground">{bResult.dibuat}</p><p className="text-xs text-muted-foreground">{t('dijana')}</p></div>
                      <div className="bg-card rounded-lg p-2"><p className="text-xl sm:text-2xl font-bold text-foreground">{bResult.dilangkau}</p><p className="text-xs text-muted-foreground">{t('dilangkau')}</p></div>
                      <div className="bg-card rounded-lg p-2"><p className="text-xl sm:text-2xl font-bold text-emerald2">{bResult.dengan_diskon}</p><p className="text-xs text-muted-foreground">{t('denganDiskon')}</p></div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">{t('jatuhTempo')}: {bResult.due_date}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2"><History className="h-5 w-5 text-muted-foreground" />{t('riwayatJanaan')}</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {bHistory.length === 0 ? (
                  <p className="p-6 text-sm text-muted-foreground">{t('tiadaData')}</p>
                ) : (
                  <div className="divide-y divide-border max-h-[420px] overflow-y-auto">
                    {bHistory.map((h) => {
                      const d = h.detail_json || {};
                      return (
                        <div key={h.id} className="p-4 text-sm" data-testid={`bil-hist-${h.id}`}>
                          <div className="flex items-center justify-between">
                            <p className="font-semibold text-foreground">{d.periode} <span className="font-normal text-muted-foreground">• {t(d.jenis || 'bulanan')}</span></p>
                            <StatusPill status="lunas" />
                          </div>
                          <p className="text-muted-foreground mt-1">
                            {t('dijana')}: <b className="text-foreground">{d.dibuat}</b> • {t('dilangkau')}: {d.dilangkau} • {t('denganDiskon')}: {d.dengan_diskon}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(h.created_at).toLocaleString('ms-MY', { timeZone: 'Asia/Kuala_Lumpur' })}
                            {h.actor?.nama ? ` • ${h.actor.nama}` : ''}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* ===== Dialog Tarif ===== */}
      <CenterMorphModal open={!!dlgTarif} onOpenChange={(o) => !o && setDlgTarif(null)}>
        <CenterMorphModalContent ariaLabel={dlgTarif?.isNew ? t('tambahTarif') : t('editTarif')} className="bg-card">
          <div className="p-6">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-foreground">{dlgTarif?.isNew ? t('tambahTarif') : t('editTarif')}</h2>
            </div>
          <div className="space-y-4 py-2">
            <div><Label>{t('namaTarif')}</Label>
              <Input value={fNama} onChange={(e) => setFnama(e.target.value)} className="h-12 mt-1.5" placeholder={t('cthYuranBulanan')} data-testid="tarif-nama" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('jumlahRM')}</Label>
                <Input type="number" min="0" step="0.01" value={fJumlah} onChange={(e) => setFjumlah(e.target.value)} className="h-12 mt-1.5" data-testid="tarif-jumlah" /></div>
              <div><Label>{t('jenisTarif')}</Label>
                <Select value={fJenis} onValueChange={setFjenis}>
                  <SelectTrigger className="h-12 mt-1.5" data-testid="tarif-jenis"><SelectValue /></SelectTrigger>
                  <SelectContent>{JENIS.map((j) => <SelectItem key={j} value={j}>{t(j)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-border p-3">
              <div><p className="text-sm font-medium">{t('layakDiskon')}</p>
              <p className="text-xs text-muted-foreground">{t('layakDiskonSub')}</p></div>
              <Switch checked={fLayak} onCheckedChange={setFlayak} data-testid="tarif-layak" />
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button variant="outline" onClick={() => setDlgTarif(null)} className="flex-1 h-11">{t('batal')}</Button>
            <Button onClick={saveTarif} disabled={busy} className="flex-1 h-11 bg-brand text-white hover:bg-brand-royal" data-testid="tarif-simpan">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{t('simpan')}
            </Button>
          </div>
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>

      {/* ===== Dialog Peraturan ===== */}
      <CenterMorphModal open={!!dlgRule} onOpenChange={(o) => !o && setDlgRule(null)}>
        <CenterMorphModalContent ariaLabel={dlgRule?.isNew ? t('tambahPeraturan') : t('editPeraturan')} className="bg-card">
          <div className="p-6">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-foreground">{dlgRule?.isNew ? t('tambahPeraturan') : t('editPeraturan')}</h2>
            </div>
          <div className="space-y-4 py-2">
            <div><Label>{t('nama')}</Label>
              <Input value={rNama} onChange={(e) => setRnama(e.target.value)} className="h-12 mt-1.5" data-testid="rule-nama" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t('jenisDiskon')}</Label>
                <Select value={rJenis} onValueChange={setRjenis}>
                  <SelectTrigger className="h-12 mt-1.5" data-testid="rule-jenis"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="peratus">{t('peratus')}</SelectItem>
                    <SelectItem value="tetap_rm">{t('tetapRM')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>{t('nilaiDiskon')}</Label>
                <Input type="number" min="0" step="0.01" value={rNilai} onChange={(e) => setRnilai(e.target.value)} className="h-12 mt-1.5" data-testid="rule-nilai" /></div>
            </div>
            <div><Label>{t('anakKeMin')}</Label>
              <Input type="number" min="2" value={rAnakKe} onChange={(e) => setRanakKe(e.target.value)} className="h-12 mt-1.5" data-testid="rule-anakke" />
              <p className="text-xs text-muted-foreground mt-1">{t('anakKeMinSub')}</p>
            </div>
          </div>
          <div className="flex gap-2 mt-6">
            <Button variant="outline" onClick={() => setDlgRule(null)} className="flex-1 h-11">{t('batal')}</Button>
            <Button onClick={saveRule} disabled={busy} className="flex-1 h-11 bg-brand text-white hover:bg-brand-royal" data-testid="rule-simpan">
              {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}{t('simpan')}
            </Button>
          </div>
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>
    </div>
  );
}
